import type * as THREE from 'three';

/**
 * CAD 场景对象持久化模式。
 *
 * 设计说明：
 * 使用常量对象与联合类型表达有限状态，避免为单一持久化策略引入枚举。
 */
export const CadObjectPersistenceMode = {
  /** 对象由业务定义在运行时派生，不应写入长期模型快照。 */
  RuntimeDerived: 'runtime-derived',
  /** 对象仅是运行时容器；持久化时折叠容器自身，但保留其中的业务子树。 */
  RuntimeContainer: 'runtime-container',
} as const;

/** CAD 场景对象持久化模式类型。 */
export type CadObjectPersistenceMode = typeof CadObjectPersistenceMode[keyof typeof CadObjectPersistenceMode];

/**
 * 对象持久化模式的私有键。
 *
 * 非可枚举 Symbol 可以避免污染 `userData`、Three.js JSON 与 glTF extras，
 * 同时让导出模块只依赖明确的文档层约定。
 */
const CAD_OBJECT_PERSISTENCE_MODE_KEY = Symbol('cad-object-persistence-mode');

/** 带可选持久化模式的场景对象。 */
type CadObjectPersistenceTarget = THREE.Object3D & {
  [CAD_OBJECT_PERSISTENCE_MODE_KEY]?: CadObjectPersistenceMode;
};

/**
 * 写入一次性持久化模式。
 *
 * 持久化语义在对象创建时就应确定；禁止运行时改写可避免同一对象在两次导出中产生不同结果。
 *
 * @param object 需要标记的场景对象。
 * @param mode 持久化模式。
 * @throws 对象已标记为其他模式时抛出错误。
 */
function markCadObjectPersistenceMode(
  object: THREE.Object3D,
  mode: CadObjectPersistenceMode,
): void {
  const currentMode = readCadObjectPersistenceMode(object);
  if (currentMode === mode) {
    return;
  }
  if (currentMode !== null) {
    throw new Error(`CAD 场景对象持久化模式不可改写: ${currentMode} -> ${mode}`);
  }
  Object.defineProperty(object, CAD_OBJECT_PERSISTENCE_MODE_KEY, {
    configurable: false,
    enumerable: false,
    writable: false,
    value: mode,
  });
}

/**
 * 标记场景对象为运行时派生对象。
 *
 * 导出器遇到该对象时会跳过整棵子树；业务模块必须另行保存可重建该结果的定义。
 *
 * @param object 需要标记的场景对象。
 */
export function markCadObjectAsRuntimeDerived(object: THREE.Object3D): void {
  markCadObjectPersistenceMode(object, CadObjectPersistenceMode.RuntimeDerived);
}

/**
 * 标记场景对象为运行时容器。
 *
 * 导出器会折叠容器自身的层级和局部变换，同时继续导出其业务子级。
 *
 * @param object 需要标记的场景容器。
 */
export function markCadObjectAsRuntimeContainer(object: THREE.Object3D): void {
  markCadObjectPersistenceMode(object, CadObjectPersistenceMode.RuntimeContainer);
}

/**
 * 读取场景对象的持久化模式。
 *
 * @param object 待读取的场景对象。
 * @returns 已声明的持久化模式；未声明时返回 `null`。
 */
export function readCadObjectPersistenceMode(object: THREE.Object3D): CadObjectPersistenceMode | null {
  return (object as CadObjectPersistenceTarget)[CAD_OBJECT_PERSISTENCE_MODE_KEY] ?? null;
}

/**
 * 判断场景对象是否为运行时派生结果。
 *
 * @param object 待判断的场景对象。
 * @returns 是否应从长期模型快照中排除该对象及其子树。
 */
export function isCadObjectRuntimeDerived(object: THREE.Object3D): boolean {
  return readCadObjectPersistenceMode(object) === CadObjectPersistenceMode.RuntimeDerived;
}

/**
 * 判断对象是否为只折叠自身、保留子树的运行时容器。
 *
 * @param object 待判断的场景对象。
 * @returns 导出时是否应只跳过容器自身。
 */
export function isCadObjectRuntimeContainer(object: THREE.Object3D): boolean {
  return readCadObjectPersistenceMode(object) === CadObjectPersistenceMode.RuntimeContainer;
}
